import React, { useState } from 'react';
import axios from 'axios';
import { NavigateFunction } from 'react-router-dom';
import { useMercenary } from '../../../contexts/MercenaryContext';
import Modal from '../../Shared/Modal';
import Toast from '../../Shared/Toast';
import { savePreferencesToDatabase } from './savePreferencesToDatabase';
import { loadCookiePrefs } from './lobbyPreferences';
import styles from './ActionButtons.module.css';

interface ActionButtonsProps {
  isOwner: boolean;
  userInCurrentLobby: boolean;
  isInAnyLobby: boolean;
  navigate: NavigateFunction;
  lobbyId: string;
  matchType: string;
  onBattleStart: () => void;
  teamsFull: boolean;
  randomTeams: boolean;
  pve: boolean;
  isRanked?: boolean;
  setSelectedTeam: (team: 'teamOne' | 'teamTwo') => void;
  handleKickPlayer: (mercenaryId: number) => void;
}

const ActionButtons: React.FC<ActionButtonsProps> = ({
  isOwner,
  userInCurrentLobby,
  isInAnyLobby,
  navigate,
  lobbyId,
  matchType,
  onBattleStart,
  teamsFull,
  randomTeams,
  pve,
  isRanked = false,
}) => {
  const lobbyListPath = isRanked ? '/ranked-battle' : '/team-battle';
  const { mercenary } = useMercenary();
  const [loading, setLoading] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [showDeathModal, setShowDeathModal] = useState(false);
  const [teamStatus, setTeamStatus] = useState({ teamOne: 0, teamTwo: 0, maxPlayers: 0 });

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
  };

  const fetchTeamStatus = async () => {
    try {
      const { data: lobby } = await axios.get(`/api/team-battle/lobbies/${lobbyId}`, { withCredentials: true });
      setTeamStatus({ teamOne: lobby.teamOne.length, teamTwo: lobby.teamTwo.length, maxPlayers: lobby.maxPlayers });
    } catch (err) {
      displayToast('Failed to fetch team data.', 'error');
    }
  };

  const handleJoinLobby = async (team: 'teamOne' | 'teamTwo' | null = null) => {
    setLoading(true);
    try {
      if (!mercenary?.id) {
        displayToast('Invalid mercenary.', 'error');
        setLoading(false);
        return;
      }

      const { data: lobby } = await axios.get(`/api/team-battle/lobbies/${lobbyId}`, { withCredentials: true });
      const maxTeamSize = lobby.pve ? lobby.maxPlayers : Math.floor(lobby.maxPlayers / 2);
      const teamOneSize = lobby.teamOne.length;
      const teamTwoSize = lobby.teamTwo.length;

      if (lobby.pve) {
        if (teamOneSize >= maxTeamSize) { displayToast('The team is full.', 'error'); setLoading(false); return; }
      } else if (team === 'teamOne' && teamOneSize >= maxTeamSize) {
        displayToast('Team One is full.', 'error'); setLoading(false); return;
      } else if (team === 'teamTwo' && teamTwoSize >= maxTeamSize) {
        displayToast('Team Two is full.', 'error'); setLoading(false); return;
      }

      const selectedTeam = lobby.pve ? 'teamOne' : team;
      const battleType = lobby.pve ? 'pve' : lobby.matchType;
      const cookiePrefs = loadCookiePrefs(mercenary.id, battleType);
      const savedPreferences = {
        surrenderHP: cookiePrefs?.surrenderHP ?? 0.5,
        readyHP: cookiePrefs?.readyHP ?? 0.9,
        strategy: cookiePrefs?.strategy ?? 'Normal',
      };

      const payload = { mercenaryId: mercenary.id, team: selectedTeam, ...savedPreferences };
      await axios.post(`/api/team-battle/lobbies/${lobbyId}/join`, payload, { withCredentials: true });
      await savePreferencesToDatabase(Number(lobbyId), mercenary.id, savedPreferences);

      displayToast(`Joined ${lobby.pve ? 'the team' : selectedTeam === 'teamOne' ? 'Team One' : 'Team Two'} successfully.`, 'success');
      setShowTeamModal(false);
      navigate(`/team-battle/lobby/${lobbyId}`);
    } catch (err: any) {
      displayToast(err?.response?.data?.message ?? 'Error joining lobby.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveLobby = async () => {
    setLoading(true);
    try {
      await axios.post(`/api/team-battle/lobbies/${lobbyId}/leave`, { mercenaryId: mercenary.id }, { withCredentials: true });
      navigate(lobbyListPath);
    } catch (err: any) {
      displayToast(err?.response?.data?.message ?? 'Error leaving lobby.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLobby = async () => {
    setLoading(true);
    try {
      await axios.delete(`/api/team-battle/lobbies/${lobbyId}`, { withCredentials: true });
      navigate(lobbyListPath);
    } catch (err: any) {
      displayToast(err?.response?.data?.message ?? 'Error deleting lobby.', 'error');
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
    }
  };

  const handleJoinClick = async () => {
    if (matchType === 'death') {
      await fetchTeamStatus();
      setShowDeathModal(true);
    } else if (randomTeams || pve) {
      handleJoinLobby();
    } else {
      await fetchTeamStatus();
      setShowTeamModal(true);
    }
  };

  const handleDeathConfirm = async () => {
    setShowDeathModal(false);
    if (randomTeams || pve) {
      handleJoinLobby();
    } else {
      setShowTeamModal(true);
    }
  };

  return (
    <div className={styles.actionsContainer}>
      {toastMessage && (
        <Toast message={toastMessage} type={toastType} duration={3000} onClose={() => setToastMessage(null)} />
      )}

      {!userInCurrentLobby && !isInAnyLobby && !isOwner && (
        <button className={styles.btnJoinA} onClick={handleJoinClick} disabled={loading}>
          {loading ? 'Joining…' : 'Join Battle'}
        </button>
      )}

      {userInCurrentLobby && !isOwner && (
        <button className={styles.btnLeave} onClick={handleLeaveLobby} disabled={loading}>
          {loading ? 'Leaving…' : 'Leave Lobby'}
        </button>
      )}

      {isOwner && (
        <>
          <button className={styles.btnStart} onClick={onBattleStart} disabled={loading || !teamsFull}>
            {loading ? 'Starting…' : '⚔ Start Battle'}
          </button>
          {!teamsFull && <div className={styles.hint}>Lobby must be full to start</div>}
          <button className={styles.btnLeave} onClick={() => setShowDeleteModal(true)} disabled={loading}>
            Delete Lobby
          </button>
        </>
      )}

      <button className={styles.btnBack} onClick={() => navigate(lobbyListPath, { state: { fromLobby: true } })} disabled={loading}>
        ← Back to Lobbies
      </button>

      <Modal
        show={showTeamModal}
        onClose={() => setShowTeamModal(false)}
        title="Select a Team"
        message="Choose a team to join."
        customButtons={[
          {
            text: 'Team One',
            action: () => handleJoinLobby('teamOne'),
            disabled: teamStatus.teamOne >= Math.floor(teamStatus.maxPlayers / 2),
          },
          {
            text: 'Team Two',
            action: () => handleJoinLobby('teamTwo'),
            disabled: teamStatus.teamTwo >= Math.floor(teamStatus.maxPlayers / 2),
          },
        ]}
      />

      <Modal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteLobby}
        title="Delete Lobby"
        message="Are you sure you want to delete this lobby? This cannot be undone."
      />

      <Modal
        show={showDeathModal}
        onClose={() => setShowDeathModal(false)}
        onConfirm={handleDeathConfirm}
        title="⚠ Death Battle"
        message="This is a Death Battle, if your mercenary falls in combat, they die permanently and cannot be recovered. Are you sure you want to risk it?"
      />
    </div>
  );
};

export default ActionButtons;
